import { useEffect, useRef, type KeyboardEvent } from "react";
import { currentPeriodStart } from "../hooks/useSelectedPeriod";
import styles from "./MonthPicker.module.css";

const FirstPeriod = "2025-08-01";

function periodStartFor(year: number, month: number): string {
  return `${String(year)}-${String(month + 1).padStart(2, "0")}-01`;
}

function monthOptions(): string[] {
  const first = new Date(`${FirstPeriod}T00:00:00Z`);
  const now = new Date();
  const options: string[] = [];
  const cursor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  while (cursor >= first) {
    options.push(periodStartFor(cursor.getUTCFullYear(), cursor.getUTCMonth()));
    cursor.setUTCMonth(cursor.getUTCMonth() - 1);
  }
  return options;
}

export function MonthPicker({
  periodStart,
  onChange,
  label,
  focusOnMount = false,
  onClose,
}: {
  periodStart: string;
  onChange: (periodStart: string) => void;
  label: string;
  /** Moves focus to the chosen month when the picker appears, so the arrow keys work at once. */
  focusOnMount?: boolean;
  /** Called when Escape is pressed, so the owner can close the picker and take focus back. */
  onClose?: () => void;
}): React.JSX.Element {
  const options = monthOptions();
  const selectedPeriod = options.includes(periodStart) ? periodStart : currentPeriodStart();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusOnMount) containerRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]')?.focus();
  }, [focusOnMount]);

  // One tab stop for the list (the chosen month); the arrow keys, Home and End move between months, as in any listbox.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      if (onClose) {
        event.preventDefault();
        onClose();
      }
      return;
    }
    const keys = ["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"];
    if (!keys.includes(event.key)) return;
    const items = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    if (items.length === 0) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const page = 5;
    const target =
      event.key === "Home" ? 0
      : event.key === "End" ? items.length - 1
      : event.key === "ArrowDown" ? current + 1
      : event.key === "ArrowUp" ? current - 1
      : event.key === "PageDown" ? current + page
      : current - page;
    items[Math.min(items.length - 1, Math.max(0, target))].focus();
  };

  return (
    <div ref={containerRef} className={styles.container} role="listbox" aria-label={label} onKeyDown={onKeyDown}>
      <div className={styles.options}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`${styles.option} ${option === selectedPeriod ? styles.selected : ""}`}
            role="option"
            aria-selected={option === selectedPeriod}
            tabIndex={option === selectedPeriod ? 0 : -1}
            onClick={() => onChange(option)}
          >
            <span className={styles.optionMarker} aria-hidden="true">
              {option === selectedPeriod ? "✓" : ""}
            </span>
            <span className={styles.optionLabel}>
              {new Intl.DateTimeFormat("en-US", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              }).format(new Date(`${option}T00:00:00Z`))}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
